import { Button, Position } from '@blueprintjs/core';
import { Popover2, Popover2InteractionKind, Classes as PopoverClasses } from '@blueprintjs/popover2';
import { styled } from '@moleculeone/ui';
import { Mol } from '@retro-ui/api-contract';
import { useEffect, useState } from 'react';

import { Molecule } from '@f/components/shared/molecule';
import { smilesToMol } from '@f/utils/conversion';

const CompoundContainer = styled('div', {
  'pointerEvents': 'none',
  '& .svg-mol-space': {
    width: '14rem',
    height: '14rem',
    maxWidth: '14rem',
    maxHeight: '14rem',
  },
});

interface MoleculePreviewProps {
  smiles: string;
  disabled?: boolean;
  trigger?: React.ReactNode;
  className?: string;
}

export function MoleculePreview(props: MoleculePreviewProps) {
  const { smiles, disabled, trigger, className } = props;

  const [mol, setMol] = useState<Mol | null>(null);

  useEffect(() => {
    const calculateMol = async () => {
      try {
        const newMol = await smilesToMol(smiles ?? null);
        setMol(newMol);
      } catch (error) {
        setMol(null);
      }
    };

    calculateMol();
  }, [smiles]);

  return (
    <Popover2
      className={className}
      popoverClassName={PopoverClasses.POPOVER2_CONTENT_SIZING}
      interactionKind={Popover2InteractionKind.HOVER}
      position={Position.TOP}
      hoverOpenDelay={0}
      hoverCloseDelay={0}
      transitionDuration={50}
      disabled={disabled || !smiles}
      content={
        <CompoundContainer>
          {mol ? <Molecule smiles={smiles} mol={mol} toolbox={false} /> : 'Molecule rendering unavailable'}
        </CompoundContainer>
      }
    >
      {trigger ?? <Button icon="eye-open" minimal disabled={disabled || !smiles} />}
    </Popover2>
  );
}

export function Compound(props: MoleculePreviewProps) {
  const { smiles } = props;

  const [mol, setMol] = useState<Mol | null>(null);
  const [isDrawing, setDrawing] = useState(false);
  const [drawingError, setDrawingError] = useState(false);

  useEffect(() => {
    const calculateMol = async () => {
      try {
        setDrawing(true);
        const newMol = await smilesToMol(smiles ?? null);
        setMol(newMol);
      } catch (error) {
        setMol(null);
        setDrawingError(true);
      } finally {
        setDrawing(false);
      }
    };

    calculateMol();
  }, [smiles]);

  return (
    <CompoundContainer>
      {isDrawing && 'Drawing molecule...'}
      {drawingError && 'Molecule rendering unavailable'}
      {mol && <Molecule smiles={smiles} mol={mol} toolbox={false} />}
    </CompoundContainer>
  );
}
